import { useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { DateTime } from "luxon";

import {
  useGetOrganizationAnalyticsQuery,
  useGetEvidenceAnalyticsQuery,
} from "@/redux/service";
import { AnalyticsProps } from "@/features/organizations/profile/types";
import { SharingRights } from "@/shared/utils/constants";
import BaseBarChart from "@/components_v2/Charts/Bar/BaseBarChart";
import BasePieChart from "@/components_v2/Charts/Pie/BasePieChart";
import { HookFormBaseDropdown } from "@/components_v2/FormFields/ReactHookForm/fields";
import { yupResolver } from "@hookform/resolvers/yup";
import yup from "@/libraries/yup";
import ArchiveIcon from "@/assets/icons/archive.svg?react";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";

import "../styles.css";

const Analytics = ({ organization }: AnalyticsProps) => {
  const { t } = useTranslation();

  const timeSchema = yup.object().shape({
    time: yup.string().typeError(t("form.labels.notValid")).nullable(),
  });

  const form = useForm({
    resolver: yupResolver(timeSchema),
    mode: "onChange",
    defaultValues: {
      time: "all_time",
    },
  });

  const formData = form.watch();

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  const calculateDateRange = (
    timeValue: string | null | undefined,
  ): { startDate: string | undefined; endDate: string | undefined } => {
    if (!timeValue || timeValue === "all_time") {
      return { startDate: undefined, endDate: undefined };
    }

    if (/^\d{4}$/.test(timeValue)) {
      const year = parseInt(timeValue);
      return {
        startDate:
          DateTime.fromObject({ year, month: 1, day: 1 }).toISODate() ||
          undefined,
        endDate:
          DateTime.fromObject({ year, month: 12, day: 31 }).toISODate() ||
          undefined,
      };
    }

    return { startDate: undefined, endDate: undefined };
  };

  useEffect(() => {
    const dateRange = calculateDateRange(formData.time);
    setStartDate(dateRange.startDate ?? undefined);
    setEndDate(dateRange.endDate ?? undefined);
  }, [formData.time]);

  const { data: organizationAnalytics } = useGetOrganizationAnalyticsQuery({
    organizationId: organization.id,
    startDate,
    endDate,
  });

  const { data: evidenceAnalytics, isLoading: evidenceAnalyticsIsLoading } =
    useGetEvidenceAnalyticsQuery({
      organizationId: organization.id,
      startDate,
      endDate,
    });

  const evidencePerSharingRightsChartData = useMemo(() => {
    if (!evidenceAnalytics) return { isEmpty: true, datasets: [], labels: [] };
    const filteredData = evidenceAnalytics.perSharingRights.filter(
      (item) => item.count > 0,
    );

    if (filteredData.length === 0) {
      return { isEmpty: true, datasets: [], labels: [] };
    }
    return {
      isEmpty: false,
      labels: filteredData.map(
        (item) => SharingRights.getItemByValue(item.label).label,
      ),
      datasets: [
        {
          data: filteredData.map((item) => item.count),
          backgroundColor: filteredData.map(
            (item) => SharingRights.getItemByValue(item.label).color,
          ),
          borderColor: "#FFFFFF",
          borderWidth: 6,
          hoverBorderWidth: 0,
          cutout: "50%",
          strokeColors: filteredData.map(
            (item) => SharingRights.getItemByValue(item.label).color,
          ),
          totalCount: evidenceAnalytics.totalCount,
        },
      ],
    };
  }, [evidenceAnalytics]);

  const evidencePerSectionChartData = useMemo(() => {
    if (!evidenceAnalytics) return { isEmpty: true, datasets: [], labels: [] };
    const maxCount = evidenceAnalytics.perSection.reduce(
      (max, item) => Math.max(max, item.count),
      0,
    );
    return {
      maxCount,
      total: evidenceAnalytics.perSection.reduce(
        (sum, item) => sum + item.count,
        0,
      ),
      labels: evidenceAnalytics.perSection.map((item) => item.label),
      datasets: [
        {
          data: evidenceAnalytics.perSection.map(() =>
            Math.ceil(maxCount * 1.2),
          ),
          backgroundColor: "#F3F3F7",
          stack: "stack1",
          order: 2,
        },
        {
          data: evidenceAnalytics.perSection.map((item) => item.count),
          backgroundColor: "#5B92E5",
          stack: "stack1",
          order: 1,
        },
      ],
    };
  }, [evidenceAnalytics]);

  const evidencePerTemplateChartData = useMemo(() => {
    if (!evidenceAnalytics) return { isEmpty: true, datasets: [], labels: [] };
    const maxCount = evidenceAnalytics.perTemplate.reduce(
      (max, item) => Math.max(max, item.count),
      0,
    );
    return {
      maxCount,
      total: evidenceAnalytics.perTemplate.reduce(
        (sum, item) => sum + item.count,
        0,
      ),
      labels: evidenceAnalytics.perTemplate.map((item) => item.label),
      datasets: [
        {
          data: evidenceAnalytics.perTemplate.map(() =>
            Math.ceil(maxCount * 1.2),
          ),
          backgroundColor: "#F3F3F7",
          hoverBackgroundColor: "#F3F3F7",
          stack: "stack1",
          order: 2,
        },
        {
          data: evidenceAnalytics.perTemplate.map((item) => item.count),
          backgroundColor: "#7A9E54",
          stack: "stack1",
          order: 1,
        },
      ],
    };
  }, [evidenceAnalytics]);

  if (evidenceAnalyticsIsLoading) {
    return (
      <div className="analytics-loading-container">
        <BaseSpinner size="large" />
      </div>
    );
  }

    const currentYear = new Date().getFullYear();

  const timeOptions = [
      { label: t("organizations.labels.allTime"), value: "all_time" },
      ...Array.from({ length: currentYear - 2024 + 1 }, (_, i) => {
        const year = currentYear - i;
        return { label: `${year}`, value: `${year}` };
      }),
    ];

  const getTimeLabel = (value: string | null | undefined) => {
    const option = timeOptions.find((option) => option.value === value);
    return option?.label || value || "";
  };

  const emptyStateBlock = (
    <div className="analytics-chart">
      <h3 className="body-m-medium">
        {t("organizations.labels.evidencePerSharingRights")}
      </h3>

      <div className="empty-state-content flex flex-col gap-100 items-center justify-center full-height">
        <BaseSvgRenderer component={ArchiveIcon} color="var(--neutral-700)" />

        <p className="body-m-medium">
          {t("organizations.labels.noDataAvailable")}
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-400">
      <div className="flex justify-between items-center flex-wrap">
        <h1 className="header-l-medium">
          {t("organizations.labels.dashboard", {
            filter: getTimeLabel(formData.time),
          })}
        </h1>

        <HookFormBaseDropdown
          name="time"
          options={timeOptions}
          control={form.control}
        />
      </div>

      <div className="analytics-container-overview-cards gap-300">
        <div className="analytics-container-overview-card submitted-reports flex flex-col items-start justify-center full-width">
          <h2 className="header-xxl-medium">
            {organization.submittedSurveysCount}
          </h2>
          <p className="body-m-medium">
            {t("organizations.labels.submittedReports")}
          </p>
        </div>

        <div className="analytics-container-overview-card members flex flex-col items-start justify-center full-width">
          <h2 className="header-xxl-medium">
            {organizationAnalytics?.totalMembers}
          </h2>
          <p className="body-m-medium">
            {t("organizations.labels.prmeMembers")}
          </p>
        </div>

        <div className="analytics-container-overview-card evidence flex flex-col items-start justify-center full-width">
          <h2 className="header-xxl-medium">{evidenceAnalytics?.totalCount}</h2>
          <p className="body-m-medium">
            {t("organizations.labels.createdEvidence")}
          </p>
        </div>

        <div className="analytics-container-overview-card member-since flex flex-col items-start justify-center full-width">
          <h2 className="header-xxl-medium">
            {organization.prmeMemberSince
              ? DateTime.fromISO(organization.prmeMemberSince).toFormat("yyyy")
              : "-"}
          </h2>
          <p className="body-m-medium">
            {t("organizations.labels.memberSince")}
          </p>
        </div>
      </div>

      <div className="analytics-container-graphs gap-300 full-width">
        {evidencePerSharingRightsChartData.isEmpty ? (
          emptyStateBlock
        ) : (
          <BasePieChart
            height={400}
            data={evidencePerSharingRightsChartData}
            title={t("organizations.labels.evidencePerSharingRights")}
            className="analytics-chart"
          />
        )}

        <BaseBarChart
          height={400}
          data={evidencePerSectionChartData}
          title={t("organizations.labels.evidencePerSection")}
          className="analytics-chart"
        />

        <BaseBarChart
          height={700}
          data={evidencePerTemplateChartData}
          customOptions={{ indexAxis: "y" }}
          className="analytics-chart-column-width analytics-chart"
          title={t("organizations.labels.evidencePerEvidenceType")}
        />
      </div>
    </div>
  );
};

export default Analytics;
